import { alpha, type Theme } from '@mui/material';

export const covWidePageContent = (theme: Theme) => ({
  width: '100%',
  maxWidth: '1720px',
  margin: '0 auto',
  paddingLeft: { xs: theme.spacing(1.5), sm: theme.spacing(3), lg: theme.spacing(4) },
  paddingRight: { xs: theme.spacing(1.5), sm: theme.spacing(3), lg: theme.spacing(4) },
  boxSizing: 'border-box',
  minWidth: 0,
});

export const covElevatedSurfaceBase = (theme: Theme) => ({
  borderRadius: theme.spacing(1),
  border: 'none',
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.palette.shadow.surface,
});

export const covDialogBackdrop = (theme: Theme) => ({
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
  backgroundColor: alpha(theme.palette.common.white, 0.08),
});

export const covDialogPaper = (theme: Theme) => ({
  backgroundColor: 'var(--main-bg-color)',
  border: `1px solid ${theme.palette.border.subtle}`,
  borderRadius: theme.spacing(2.25),
  boxShadow: theme.palette.shadow.modal,
});

export const commonStyles = {
  pageRoot: (theme: Theme) => ({
    minHeight: '100vh',
    background: theme.palette.background.default,
    paddingTop: { xs: theme.spacing(2), sm: theme.spacing(4) },
    paddingBottom: { xs: theme.spacing(2), sm: theme.spacing(4) },
    fontFamily: 'Roboto, sans-serif',
    overflowX: 'hidden',
  }),
  centeredLoader: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'center',
    mt: theme.spacing(10),
  }),
  header: (theme: Theme) => ({
    mb: { xs: theme.spacing(3), sm: theme.spacing(4) },
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    flexWrap: 'wrap',
  }),
  title: (theme: Theme) => ({
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: { xs: '1.5rem', sm: '2rem' },
  }),
  subtitle: (theme: Theme) => ({
    mt: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
  }),
  content: (theme: Theme) => ({
    width: '100%',
    minWidth: 0,
    mt: { xs: theme.spacing(3), sm: theme.spacing(4) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: theme.spacing(3), sm: theme.spacing(4) },
  }),
  loadingWrap: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  loadingMask: (theme: Theme) => ({
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.common.white, 0.56),
  }),
  backRow: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    mb: theme.spacing(2),
  }),
  backButton: (theme: Theme) => ({
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: theme.spacing(1),
    alignSelf: 'flex-start',
  }),
  statusChip: (theme: Theme) => ({
    minWidth: 89,
    height: 27,
    borderRadius: '100px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    '& .MuiChip-label': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      lineHeight: '19px',
    },
    '&.MuiChip-colorDefault': {
      backgroundColor: `${theme.palette.primary['200']} !important`,
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: `${theme.palette.primary['200']} !important`,
      },
    },
    '&.MuiChip-colorPrimary': {
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: theme.palette.common.white,
    },
    '&.MuiChip-colorInfo': {
      backgroundColor: `${theme.palette.info.main} !important`,
      color: theme.palette.common.white,
    },
    '&.MuiChip-colorSuccess': {
      backgroundColor: `${theme.palette.success.main} !important`,
      color: theme.palette.common.white,
    },
    '&.MuiChip-colorError': {
      backgroundColor: `${theme.palette.error.main} !important`,
      color: theme.palette.common.white,
    },
    '&.MuiChip-colorWarning': {
      backgroundColor: `${theme.palette.warning.main} !important`,
      color: theme.palette.common.white,
    },
  }),
} as const;


